import { useEffect, useRef, useState } from 'react'

import type { FilterOperator } from 'types/filter-page'

type LetterErrorSlot = 'single' | 'start' | 'end'

type UseNumericValidationParams = {
  operator: FilterOperator
  onValidityChange?: (_isValid: boolean) => void
}

const NO_ERRORS: Record<LetterErrorSlot, boolean> = {
  single: false,
  start: false,
  end: false,
}

// How long the "only numbers" warning stays visible after a rejected letter.
const LETTER_ERROR_FLASH_MS = 1500

/**
 * Tracks letter-input errors for the number filter inputs: strips non-digits so
 * a letter can never enter the value, and briefly flags the offending input.
 * The flag is a flash (the letter is already gone from the value), so it
 * auto-clears — otherwise it would block submission of a now-valid value.
 */
export const useNumericValidation = ({
  operator,
  onValidityChange,
}: UseNumericValidationParams) => {
  const [letterErrors, setLetterErrors] =
    useState<Record<LetterErrorSlot, boolean>>(NO_ERRORS)
  // One flash timer per slot, so typing in one input never resets another's.
  const flashTimeouts = useRef<
    Partial<Record<LetterErrorSlot, ReturnType<typeof setTimeout>>>
  >({})

  const clearAllTimers = () => {
    Object.values(flashTimeouts.current).forEach(id => clearTimeout(id))
    flashTimeouts.current = {}
  }

  // Reset transient letter errors whenever the operator changes.
  useEffect(() => {
    clearAllTimers()
    setLetterErrors(NO_ERRORS)
  }, [operator])

  // Clear any pending flash timers on unmount.
  useEffect(() => () => clearAllTimers(), [])

  const hasActiveLetterError =
    operator === 'between'
      ? letterErrors.start || letterErrors.end
      : letterErrors.single

  // A typed letter is stripped from the value, so the badge can't see it.
  // Report it here so submission stays blocked while the warning shows.
  useEffect(() => {
    onValidityChange?.(!hasActiveLetterError)
  }, [hasActiveLetterError, onValidityChange])

  const handleDigitChange = (
    slot: LetterErrorSlot,
    raw: string,
    apply: (_digits: string) => void,
  ) => {
    const digits = raw.replace(/\D/g, '')
    const hasLetter = digits !== raw
    apply(digits)

    clearTimeout(flashTimeouts.current[slot])

    if (hasLetter) {
      // Flash this slot's warning, then auto-clear just this slot so the
      // (already valid) value stays submittable even if the user stops typing.
      setLetterErrors(prev => (prev[slot] ? prev : { ...prev, [slot]: true }))
      flashTimeouts.current[slot] = setTimeout(() => {
        delete flashTimeouts.current[slot]
        setLetterErrors(prev =>
          prev[slot] ? { ...prev, [slot]: false } : prev,
        )
      }, LETTER_ERROR_FLASH_MS)
    } else {
      // A valid digit clears this input's warning immediately.
      delete flashTimeouts.current[slot]
      setLetterErrors(prev => (prev[slot] ? { ...prev, [slot]: false } : prev))
    }
  }

  return { letterErrors, hasActiveLetterError, handleDigitChange }
}
